import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData, createTableStructuredData } from '../../utils/structuredData';

export default function Comparison() {
  // SEO設定
  usePageSEO({
    title: 'DMM FX vs GMOクリック証券 徹底比較 | スプレッド・手数料・ツール',
    description: 'DMM FXとGMOクリック証券を徹底比較。スプレッド、取引条件、サービス、ツールを詳しく比較表で解説。初心者にはDMM FXがおすすめな理由、それぞれの強みと選び方のポイントを紹介します。',
    keywords: 'DMM FX,GMOクリック証券,比較,スプレッド,手数料,取引ツール,FX会社,選び方',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FX vs GMOクリック証券 徹底比較',
        description: 'DMM FXとGMOクリック証券のスプレッド、手数料、ツール、サービスを詳しく比較。どちらを選ぶべきか解説します。',
        author: 'DMM FX情報局',
        datePublished: '2024-11-10',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/28954683d35976610ae8c9b0a6a2eb36.jpg',
        url: '/comparison',
      }),
      createFAQStructuredData([
        {
          question: 'DMM FXとGMOクリック証券、どちらを選ぶべきですか？',
          answer: '初心者の方にはDMM FXをおすすめします。24時間サポート、LINEサポート、取引応援ポイント、取引通信簿など、初心者に優しいサービスが充実しています。一方、1,000通貨から少額取引したい方や、CFDやバイナリーオプションも取引したい方にはGMOクリック証券が向いています。',
        },
        {
          question: 'スプレッドに違いはありますか？',
          answer: '主要通貨ペアのスプレッドはほぼ同水準です。米ドル/円、ユーロ/円、ポンド/円、豪ドル/円などの人気通貨ペアでは両社とも業界最狭水準のスプレッドを提供しています。',
        },
        {
          question: '最小取引単位の違いは何ですか？',
          answer: 'DMM FXは10,000通貨から、GMOクリック証券は1,000通貨から取引可能です。少額から始めたい初心者の方にはGMOクリック証券が有利ですが、10,000通貨以上で取引する予定の方にはDMM FXの充実したサポートがおすすめです。',
        },
        {
          question: 'サポート体制に違いはありますか？',
          answer: '両社とも24時間サポートを提供していますが、DMM FXはLINEでの問い合わせにも対応しており、より気軽に質問できる環境が整っています。初心者の方にとってはこの点が大きなメリットとなります。',
        },
      ]),
      createTableStructuredData({
        about: 'DMM FXとGMOクリック証券のスプレッド、取引条件、サービスの比較表',
        description: '主要通貨ペアのスプレッド、最小取引単位、通貨ペア数、レバレッジ、サポート時間、デモ口座、スマホアプリ、LINEサポートなどを詳細に比較',
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '他社比較', url: '/comparison' },
      ]),
    ],
  });

  const comparisonData = [
    {
      category: 'スプレッド',
      items: [
        { name: 'USD/JPY', dmm: '0.2銭', gmo: '0.2銭', winner: 'draw' },
        { name: 'EUR/JPY', dmm: '0.4銭', gmo: '0.4銭', winner: 'draw' },
        { name: 'GBP/JPY', dmm: '0.9銭', gmo: '0.9銭', winner: 'draw' },
        { name: 'AUD/JPY', dmm: '0.5銭', gmo: '0.5銭', winner: 'draw' },
      ],
    },
    {
      category: '取引条件',
      items: [
        { name: '最小取引単位', dmm: '10,000通貨', gmo: '1,000通貨', winner: 'gmo' },
        { name: '通貨ペア数', dmm: '21通貨ペア', gmo: '20通貨ペア', winner: 'dmm' },
        { name: 'レバレッジ', dmm: '最大25倍', gmo: '最大25倍', winner: 'draw' },
        { name: '取引手数料', dmm: '無料', gmo: '無料', winner: 'draw' },
      ],
    },
    {
      category: 'サービス',
      items: [
        { name: 'サポート時間', dmm: '24時間', gmo: '24時間', winner: 'draw' },
        { name: 'デモ口座', dmm: 'あり', gmo: 'あり', winner: 'draw' },
        { name: 'スマホアプリ', dmm: 'iOS/Android', gmo: 'iOS/Android', winner: 'draw' },
        { name: 'LINEサポート', dmm: 'あり', gmo: 'なし', winner: 'dmm' },
      ],
    },
  ];

  const features = [
    {
      title: 'DMM FXが優れている点',
      icon: 'ri-trophy-line',
      color: 'teal',
      items: [
        '取引応援ポイントサービスで取引するほどお得',
        'LINEで気軽に問い合わせ可能',
        '取引通信簿で自分の取引を分析できる',
        '平日24時間電話サポート対応',
        '業界最狭水準のスプレッド',
      ],
    },
    {
      title: 'GMOクリック証券が優れている点',
      icon: 'ri-star-line',
      color: 'gray',
      items: [
        '1,000通貨から取引可能（少額取引向き）',
        'FX取引高世界第1位の実績',
        '高機能な取引ツール「はっちゅう君FX+」',
        'バイナリーオプションも取引可能',
        'CFD取引も同じ口座で可能',
      ],
    },
  ];

  const recommendations = [
    {
      title: 'DMM FXがおすすめな人',
      icon: 'ri-user-smile-line',
      color: 'from-teal-500 to-emerald-600',
      items: [
        '10,000通貨以上で取引する方',
        'ポイントを貯えてお得に取引したい方',
        'LINEでサポートを受けたい方',
        '取引分析ツールを活用したい方',
        '初心者で手厚いサポートが欲しい方',
      ],
      cta: true,
    },
    {
      title: 'GMOクリック証券がおすすめな人',
      icon: 'ri-user-line',
      color: 'from-gray-500 to-gray-600',
      items: [
        '少額（1,000通貨）から始めたい方',
        'FX以外にCFDも取引したい方',
        'バイナリーオプションに興味がある方',
        '高機能ツールを使いこなせる方',
        '取引実績を重視する方',
      ],
      cta: false,
    },
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
      <p className="text-sm text-gray-500 mt-3">口座開設・維持費無料 / 最短5分で申込完了</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: '他社比較', url: '/comparison' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            DMM FX vs GMOクリック証券<br className="md:hidden" />徹底比較
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            スプレッド・手数料・ツール・サービスを詳しく比較
          </p>
          
          {/* CTA①：冒頭 */}
          <CTAButton />
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            詳細比較表
          </h2>
          
          <div className="space-y-12">
            {comparisonData.map((section, index) => (
              <div key={index}>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{section.category}</h3>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">項目</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-teal-600">DMM FX</th>
                          <th className="px-6 py-4 text-center text-sm font-bold text-gray-600">GMOクリック証券</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.items.map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                            <td className={`px-6 py-4 text-sm text-center font-semibold ${item.winner === 'dmm' ? 'bg-teal-50' : ''}`}>
                              {item.winner === 'dmm' ? (
                                <>
                                  <Highlight variant="bold" color="red" className="text-sm">{item.dmm}</Highlight>
                                  <i className="ri-trophy-line ml-2 text-teal-500"></i>
                                </>
                              ) : (
                                <span className="text-gray-700">{item.dmm}</span>
                              )}
                            </td>
                            <td className={`px-6 py-4 text-sm text-center font-semibold ${item.winner === 'gmo' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}>
                              {item.gmo}
                              {item.winner === 'gmo' && <i className="ri-trophy-line ml-2 text-blue-500"></i>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            それぞれの強み
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-br from-${feature.color}-500 to-${feature.color}-600 rounded-full flex items-center justify-center`}>
                    <i className={`${feature.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-gray-700">
                      <i className={`ri-check-line text-${feature.color}-500 mr-2 flex-shrink-0 mt-1`}></i>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* DMM FX評判へのリンク */}
          <div className="mt-12 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-chat-quote-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">実際の利用者の声を確認</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  DMM FXの実際の利用者の口コミ・評判、メリット・デメリットについては、こちらの記事で詳しくまとめています。
                </p>
                <Link 
                  to="/reviews" 
                  className="inline-flex items-center text-teal-600 font-bold hover:text-teal-700 underline cursor-pointer text-base"
                >
                  <span>→ DMM FXの口コミ・評判を詳しく見る</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* CTA②：中盤 */}
          <div className="mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* Recommendations Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            どちらを選ぶべき？
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {recommendations.map((rec, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border-2 border-gray-200">
                <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${rec.color} text-white px-4 py-2 rounded-full mb-6`}>
                  <i className={`${rec.icon} text-lg`}></i>
                  <span className="font-bold text-sm">{rec.title}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {rec.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-gray-700">
                      <i className="ri-arrow-right-s-line text-teal-500 mr-2 flex-shrink-0 mt-1"></i>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                {rec.cta && (
                  <div className="text-center">
                    <a 
                      href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
                      rel="nofollow"
                      className="inline-block bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                    >
                      DMM FXで口座開設する
                    </a>
                    <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">関連記事</h2>
            <p className="text-gray-600 text-base">もっと詳しく知りたい方へ</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/spread" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-funds-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">スプレッドは本当に狭い？</h3>
              <p className="text-sm text-gray-600 mb-4">取引コストを他社と徹底比較</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/disadvantages" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">DMM FXのデメリット</h3>
              <p className="text-sm text-gray-600 mb-4">知っておきたい注意点を正直に解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/how-to-choose" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-search-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">FX会社の選び方</h3>
              <p className="text-sm text-gray-600 mb-4">自分に合ったFX会社を見つける方法</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/campaign" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-gift-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">キャンペーン情報</h3>
              <p className="text-sm text-gray-600 mb-4">最大50万円キャッシュバックの詳細</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              結論：<Highlight variant="marker" color="red" className="text-2xl">初心者にはDMM FXがおすすめ</Highlight>
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              両社とも業界トップクラスのFX会社ですが、<Highlight variant="bold" color="red">初心者の方にはDMM FXをおすすめします</Highlight>。理由は以下の通りです：
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start text-gray-700">
                <i className="ri-check-double-line text-teal-500 mr-2 flex-shrink-0 mt-1"></i>
                <span className="text-sm"><strong>24時間サポート</strong>で初心者でも安心して取引できる</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-check-double-line text-teal-500 mr-2 flex-shrink-0 mt-1"></i>
                <span className="text-sm"><strong>LINEサポート</strong>で気軽に質問できる</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-check-double-line text-teal-500 mr-2 flex-shrink-0 mt-1"></i>
                <span className="text-sm"><strong>取引応援ポイント</strong>で取引するほどお得</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-check-double-line text-teal-500 mr-2 flex-shrink-0 mt-1"></i>
                <span className="text-sm"><strong>取引通信簿</strong>で自分の取引を振り返れる</span>
              </li>
            </ul>

            {/* 口座開設手順へのリンク */}
            <div className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
              <p className="text-gray-700 text-sm leading-relaxed">
                口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
                → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
              </p>
            </div>

            {/* CTA③：結論部分 */}
            <CTAButton />
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}